"use client"

import { useMemo } from "react"
import { View, StyleSheet } from "react-native"
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip } from "recharts"
import { Text } from "react-native-paper"
import { HorizontalSection } from "../../../NodeSummary/HorizontalSection"
import type { TabComponentProps } from "../../../../types/tabs"
import { useUnits } from "../../../../contexts/UnitsContext"
import convert from "convert-units"

interface ChartData {
  name: string
  value: number
  color: string
}

// Define a color palette that will be used for any tower types
const COLOR_PALETTE = [
  "#4361ee", // Blue
  "#ef476f", // Pink/Red
  "#06d6a0", // Teal
  "#ffd60a", // Yellow
  "#8338ec", // Purple
  "#ff9e00", // Orange
  "#3a86ff", // Royal Blue
  "#fb5607", // Vermilion
  "#118ab2", // Steel Blue
  "#073b4c", // Dark Teal
]

export function TowerDetailsSection({ selectedNode, projectData }: TabComponentProps) {
  // Get units context
  const { activeUnits } = useUnits()

  // Default weight unit is kg
  const defaultWeightUnit = "kg"
  // Get the active weight unit from context or use default
  const weightUnit = activeUnits.mass || defaultWeightUnit

  // Function to convert weight values using convert-units
  const convertWeightValue = (value: number) => {
    try {
      const convertedValue = convert(value).from(defaultWeightUnit).to(weightUnit)
      return convertedValue.toFixed(0)
    } catch (error) {
      // Could not convert weight unit
      return value.toFixed(0)
    }
  }

  const { quantityData, weightData } = useMemo(() => {
    if (!selectedNode || !projectData?.towerWeights?.Sections) {
      return { quantityData: [], weightData: [] }
    }

    const findRelevantWeightInfo = () => {
      const sections = projectData.towerWeights.Sections

      if (selectedNode.type === "line") {
        return sections.find((section) => section.WeightInfo.Name === "Line")?.WeightInfo
      } else if (selectedNode.type === "section") {
        return sections.find((section) => section.WeightInfo.Name === selectedNode.name)?.WeightInfo
      }
      return null
    }

    const relevantWeightInfo = findRelevantWeightInfo()
    if (!relevantWeightInfo?.WeightInfo) {
      return { quantityData: [], weightData: [] }
    }

    const towerWeights = relevantWeightInfo.WeightInfo.filter((info) => info.Type.toLowerCase() === "tower")

    const typeQuantities: Record<string, number> = {}
    const typeWeights: Record<string, number> = {}

    towerWeights.forEach((tower) => {
      const type = tower.Name || "Unknown"
      typeQuantities[type] = (typeQuantities[type] || 0) + (tower.Quantity || 0)
      typeWeights[type] = (typeWeights[type] || 0) + (tower.Total || 0)
    })

    // Process quantity data with colors based on index
    const quantityData: ChartData[] = Object.entries(typeQuantities).map(([name, value], index) => ({
      name,
      value,
      color: COLOR_PALETTE[index % COLOR_PALETTE.length],
    }))

    // Process weight data with colors based on index
    const weightData: ChartData[] = Object.entries(typeWeights).map(([name, value], index) => ({
      name,
      value,
      color: COLOR_PALETTE[index % COLOR_PALETTE.length],
    }))

    return { quantityData, weightData }
  }, [selectedNode, projectData])

  // Calculate totals for center labels
  const totalQuantity = quantityData.reduce((sum, item) => sum + item.value, 0)
  const totalWeight = weightData.reduce((sum, item) => sum + item.value, 0)

  // Convert the total weight for display
  const convertedTotalWeight = convertWeightValue(totalWeight)

  // Custom tooltip for weight chart with unit conversion
  const CustomWeightTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const entry = payload[0]
      const convertedValue = convertWeightValue(entry.value)
      return (
        <View style={tooltipStyles.container}>
          <Text style={tooltipStyles.label}>{entry.name}</Text>
          <Text style={tooltipStyles.value}>
            {convertedValue} {weightUnit}
          </Text>
          <Text style={tooltipStyles.percentage}>({((entry.value / totalWeight) * 100).toFixed(1)}%)</Text>
        </View>
      )
    }
    return null
  }

  // Custom tooltip for quantity chart
  const CustomQuantityTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const entry = payload[0]
      return (
        <View style={tooltipStyles.container}>
          <Text style={tooltipStyles.label}>{entry.name}</Text>
          <Text style={tooltipStyles.value}>{entry.value} units</Text>
          <Text style={tooltipStyles.percentage}>({((entry.value / totalQuantity) * 100).toFixed(1)}%)</Text>
        </View>
      )
    }
    return null
  }

  const content = (
    <View style={styles.chartsContainer}>
      <View style={styles.chartWrapper}>
        <Text style={styles.chartTitle}>Tower Quantity Distribution</Text>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={quantityData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={120}
              paddingAngle={2}
            >
              {quantityData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomQuantityTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry) => `${value}: ${entry.payload.value}`}
            />
          </PieChart>
        </ResponsiveContainer>
        {quantityData.length > 0 && (
          <View style={styles.centerLabel}>
            <Text style={styles.centerValue}>{totalQuantity}</Text>
            <Text style={styles.centerText}>Total</Text>
          </View>
        )}
      </View>

      <View style={styles.chartWrapper}>
        <Text style={styles.chartTitle}>Tower Weight Distribution</Text>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={weightData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={120}
              paddingAngle={2}
            >
              {weightData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomWeightTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry) => {
                const convertedValue = convertWeightValue(entry.payload.value)
                return `${value}: ${convertedValue} ${weightUnit}`
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        {weightData.length > 0 && (
          <View style={styles.centerLabel}>
            <Text style={styles.centerValue}>{convertedTotalWeight}</Text>
            <Text style={styles.centerText}>{weightUnit} Total</Text>
          </View>
        )}
      </View>
    </View>
  )

  return (
    <HorizontalSection
      title="Tower Details"
      content={{
        "Tower Distribution": {
          "Quantity Distribution": { value: "Chart 1" },
          "Weight Distribution": { value: "Chart 2" },
        },
      }}
      defaultExpanded={true}
      showBorders={false}
    >
      {content}
    </HorizontalSection>
  )
}

const styles = StyleSheet.create({
  chartsContainer: {
    flexDirection: "row",
    gap: 24,
    flexWrap: "wrap",
  },
  chartWrapper: {
    flex: 1,
    minWidth: 300,
    alignItems: "center",
    position: "relative",
  },
  chartTitle: {
    fontWeight: "bold",
    fontSize: 16,
    marginBottom: 8,
    textAlign: "center",
  },
  centerLabel: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: [{ translateX: -50 }, { translateY: -30 }],
    alignItems: "center",
  },
  centerValue: {
    fontSize: 24,
    fontWeight: "bold",
  },
  centerText: {
    fontSize: 12,
    opacity: 0.7,
  },
})

const tooltipStyles = StyleSheet.create({
  container: {
    backgroundColor: "white",
    padding: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#ccc",
    minWidth: 150,
  },
  label: {
    fontWeight: "bold",
    marginBottom: 4,
    fontSize: 14,
  },
  value: {
    fontSize: 13,
    fontWeight: "500",
  },
  percentage: {
    fontSize: 12,
    opacity: 0.7,
    marginTop: 2,
  },
})

